import { executeNode, InsufficientSparksError } from '../../api/client'
import type { ExecuteNodeAnalytics } from '../../analytics/nodeAnalytics'
import { isApiRecord, type ApiValue } from '../../api/responseDecoders'
import { createNadDebug } from '../../utils/debugLog'
import { trackGenerationCancelled } from '../../analytics/interactionAnalytics'

export const GENERATION_GRACE_PERIOD_MS = 3_000

const STORAGE_PREFIX = 'dw.media-generation-grace.v1.'
const SUBMISSION_ACK_TIMEOUT_MS = 60_000
const log = createNadDebug('dw:generation-grace-period:client')

export type GracePeriodNodeType = 'image_generator' | 'video_generator'
export type GenerationGracePhase = 'waiting' | 'submitting' | 'failed'

type GenerationRequestFailure =
  | {
      code: 'insufficient_sparks'
      message: string
      balance: number
      required: number
    }
  | { code: 'request_failed'; message: string }

export type GenerationGraceFailure =
  | GenerationRequestFailure
  | { code: 'submission_unconfirmed' }

export interface GenerationGraceRecord {
  version: 1
  requestId: string
  ownerId: string
  projectId: string
  canvasId: string
  nodeId: string
  nodeType: GracePeriodNodeType
  count: number
  createdAtMs: number
  submitAtMs: number
  phase: GenerationGracePhase
  submittedAtMs?: number
  failure?: GenerationGraceFailure
}

export interface ScheduleGenerationInput {
  ownerId: string
  projectId: string
  canvasId: string
  nodeId: string
  nodeType: GracePeriodNodeType
  count: number
  /**
   * RFC-1577 `node_generation_submitted` context. Held in memory only
   * (never persisted with the record): a submission restored after a reload
   * still logs, with just the node type.
   */
  analytics?: ExecuteNodeAnalytics
}

export class GenerationGracePeriodStorageError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'GenerationGracePeriodStorageError'
  }
}

class GenerationCancelledBeforeDispatch extends Error {
  constructor() {
    super('Generation was cancelled before backend submission')
    this.name = 'GenerationCancelledBeforeDispatch'
  }
}

type Listener = () => void
type Timer = ReturnType<typeof setTimeout>

const records = new Map<string, GenerationGraceRecord>()
const loadedKeys = new Set<string>()
const listeners = new Map<string, Set<Listener>>()
const timers = new Map<string, Timer>()
const submitAnalytics = new Map<string, ExecuteNodeAnalytics>()
let activeOwnerId: string | null = null

function storage(): Storage {
  try {
    if (typeof window === 'undefined') {
      throw new Error('window is unavailable')
    }
    return window.sessionStorage
  } catch (error) {
    throw new GenerationGracePeriodStorageError(
      `The 3-second generation window requires session storage: ${String(error)}`,
    )
  }
}

function browserNavigationType(): string {
  if (typeof performance === 'undefined') return 'unavailable'
  const navigation = performance.getEntriesByType('navigation')[0]
  if (navigation !== undefined && 'type' in navigation && typeof navigation.type === 'string') {
    return navigation.type
  }
  return 'unknown'
}

function requestKeyParts(
  ownerId: string,
  projectId: string,
  canvasId: string,
  nodeId: string,
): string {
  return [ownerId, projectId, canvasId, nodeId].map(encodeURIComponent).join('.')
}

export function generationGraceRequestKey(
  ownerId: string,
  projectId: string,
  canvasId: string,
  nodeId: string,
): string {
  return `${STORAGE_PREFIX}${requestKeyParts(ownerId, projectId, canvasId, nodeId)}`
}

function isFiniteTimestamp(value: ApiValue): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
}

function isFailure(value: ApiValue): value is GenerationGraceFailure {
  if (!isApiRecord(value)) return false
  if (value.code === 'submission_unconfirmed') {
    return value.message === undefined
  }
  if (value.code === 'request_failed') return typeof value.message === 'string'
  return (
    value.code === 'insufficient_sparks' &&
    typeof value.message === 'string' &&
    typeof value.balance === 'number' &&
    typeof value.required === 'number'
  )
}

function parseRecord(raw: string | null): GenerationGraceRecord | null {
  if (raw === null) return null
  let value: ApiValue
  try {
    value = JSON.parse(raw)
  } catch {
    return null
  }
  if (!isApiRecord(value)) return null
  if (
    value.version !== 1 ||
    typeof value.requestId !== 'string' ||
    typeof value.ownerId !== 'string' ||
    typeof value.projectId !== 'string' ||
    typeof value.canvasId !== 'string' ||
    typeof value.nodeId !== 'string' ||
    (value.nodeType !== 'image_generator' && value.nodeType !== 'video_generator') ||
    typeof value.count !== 'number' ||
    !Number.isInteger(value.count) ||
    value.count < 1 ||
    value.count > 6 ||
    !isFiniteTimestamp(value.createdAtMs) ||
    !isFiniteTimestamp(value.submitAtMs) ||
    (value.phase !== 'waiting' &&
      value.phase !== 'submitting' &&
      value.phase !== 'failed') ||
    (value.submittedAtMs !== undefined && !isFiniteTimestamp(value.submittedAtMs)) ||
    (value.failure !== undefined && !isFailure(value.failure))
  ) {
    return null
  }
  if (value.phase === 'failed' && value.failure === undefined) return null
  if (value.phase === 'submitting' && value.submittedAtMs === undefined) return null
  return {
    version: 1,
    requestId: value.requestId,
    ownerId: value.ownerId,
    projectId: value.projectId,
    canvasId: value.canvasId,
    nodeId: value.nodeId,
    nodeType: value.nodeType,
    count: value.count,
    createdAtMs: value.createdAtMs,
    submitAtMs: value.submitAtMs,
    phase: value.phase,
    submittedAtMs: value.submittedAtMs,
    failure: value.failure,
  }
}

function saveRecord(key: string, record: GenerationGraceRecord): void {
  try {
    storage().setItem(key, JSON.stringify(record))
  } catch (error) {
    if (error instanceof GenerationGracePeriodStorageError) throw error
    throw new GenerationGracePeriodStorageError(
      `Unable to persist the generation cancellation window: ${String(error)}`,
    )
  }
}

function removeStoredRecord(key: string): void {
  try {
    storage().removeItem(key)
  } catch (error) {
    if (error instanceof GenerationGracePeriodStorageError) throw error
    throw new GenerationGracePeriodStorageError(
      `Unable to clear the generation cancellation window: ${String(error)}`,
    )
  }
}

function notify(key: string): void {
  listeners.get(key)?.forEach((listener) => listener())
}

function clearTimer(key: string): void {
  const timer = timers.get(key)
  if (timer !== undefined) clearTimeout(timer)
  timers.delete(key)
}

function putRecord(key: string, record: GenerationGraceRecord): void {
  saveRecord(key, record)
  records.set(key, record)
  loadedKeys.add(key)
  notify(key)
}

function deleteRecord(key: string): void {
  clearTimer(key)
  removeStoredRecord(key)
  records.delete(key)
  loadedKeys.add(key)
  notify(key)
}

function serializeFailure(error: Error | string): GenerationRequestFailure {
  if (error instanceof InsufficientSparksError) {
    return {
      code: 'insufficient_sparks',
      message: error.message,
      balance: error.balance,
      required: error.required,
    }
  }
  return {
    code: 'request_failed',
    message: error instanceof Error ? error.message : error,
  }
}

function markSubmissionUnconfirmed(key: string): void {
  const record = records.get(key)
  if (record?.phase !== 'submitting') return
  const failed: GenerationGraceRecord = {
    ...record,
    phase: 'failed',
    failure: {
      code: 'submission_unconfirmed',
    },
  }
  try {
    putRecord(key, failed)
  } catch {
    records.set(key, failed)
    loadedKeys.add(key)
    notify(key)
  }
  log({
    event: 'generation-grace-period:failed',
    trace_id: record.requestId,
    node_id: record.nodeId,
    node_type: record.nodeType,
    reason: failed.failure?.code,
    dispatch_outcome: 'acknowledgement_timed_out',
  })
}

function scheduleSubmissionAckTimeout(key: string, record: GenerationGraceRecord): void {
  if (record.phase !== 'submitting' || record.submittedAtMs === undefined) return
  clearTimer(key)
  const remainingMs = Math.max(
    0,
    record.submittedAtMs + SUBMISSION_ACK_TIMEOUT_MS - Date.now(),
  )
  timers.set(key, setTimeout(() => markSubmissionUnconfirmed(key), remainingMs))
}

async function dispatchGeneration(key: string, expectedRequestId: string): Promise<void> {
  const initial = records.get(key)
  if (initial?.phase !== 'waiting' || initial.requestId !== expectedRequestId) return
  clearTimer(key)

  try {
    const analytics = submitAnalytics.get(initial.requestId)
    submitAnalytics.delete(initial.requestId)
    await executeNode(initial.projectId, initial.nodeId, {
      analytics: { ...analytics, nodeType: initial.nodeType },
      requestId: initial.requestId,
      count: initial.count,
      canvasId: initial.canvasId,
      keepalive: true,
      onRequestStart: () => {
        const current = records.get(key)
        if (current?.phase !== 'waiting' || current.requestId !== expectedRequestId) {
          throw new GenerationCancelledBeforeDispatch()
        }
        const submittedAtMs = Date.now()
        const submitting: GenerationGraceRecord = {
          ...current,
          phase: 'submitting',
          submittedAtMs,
        }
        putRecord(key, submitting)
        scheduleSubmissionAckTimeout(key, submitting)
        log({
          event: 'generation-grace-period:backend-dispatch-started',
          trace_id: current.requestId,
          node_id: current.nodeId,
          node_type: current.nodeType,
          project_id: current.projectId,
          canvas_id: current.canvasId,
          scheduled_at_ms: current.createdAtMs,
          deadline_at_ms: current.submitAtMs,
          elapsed_since_schedule_ms: submittedAtMs - current.createdAtMs,
          backend_request_dispatched: true,
          dispatch_outcome: 'fetch_started',
        })
      },
    })
  } catch (error) {
    if (error instanceof GenerationCancelledBeforeDispatch) return
    const current = records.get(key)
    if (current?.requestId !== expectedRequestId) return
    const failure = serializeFailure(error instanceof Error ? error : String(error))
    const failed: GenerationGraceRecord = {
      ...current,
      phase: 'failed',
      failure,
    }
    try {
      putRecord(key, failed)
    } catch {
      records.set(key, failed)
      loadedKeys.add(key)
      notify(key)
    }
    log({
      event: 'generation-grace-period:failed',
      trace_id: current.requestId,
      node_id: current.nodeId,
      node_type: current.nodeType,
      reason: failure.code,
      message: failure.message,
      dispatch_outcome: 'request_failed',
    })
    return
  }

  const current = records.get(key)
  if (current?.requestId !== expectedRequestId) return
  deleteRecord(key)
  log({
    event: 'generation-grace-period:backend-request-accepted',
    trace_id: current.requestId,
    node_id: current.nodeId,
    node_type: current.nodeType,
    project_id: current.projectId,
    canvas_id: current.canvasId,
    backend_request_dispatched: true,
    dispatch_outcome: 'execute_node_accepted',
  })
}

function scheduleWaitingRecord(key: string, record: GenerationGraceRecord): void {
  if (record.phase !== 'waiting' || timers.has(key)) return
  const remainingMs = Math.max(0, record.submitAtMs - Date.now())
  timers.set(
    key,
    setTimeout(() => {
      void dispatchGeneration(key, record.requestId)
    }, remainingMs),
  )
}

function restoreOwnerRecords(ownerId: string): void {
  const store = storage()
  const keys: string[] = []
  for (let index = 0; index < store.length; index += 1) {
    const key = store.key(index)
    if (key?.startsWith(STORAGE_PREFIX)) keys.push(key)
  }
  keys.forEach((key) => {
    const raw = store.getItem(key)
    const record = parseRecord(raw)
    loadedKeys.add(key)
    if (record === null) {
      store.removeItem(key)
      log({ event: 'generation-grace-period:invalid-record-removed', storage_key: key })
      return
    }
    records.set(key, record)
    if (record.ownerId !== ownerId) return
    if (record.phase === 'waiting') {
      const navigationType = browserNavigationType()
      scheduleWaitingRecord(key, record)
      log({
        event: 'generation-grace-period:grace-timer-restored',
        trace_id: record.requestId,
        node_id: record.nodeId,
        node_type: record.nodeType,
        project_id: record.projectId,
        canvas_id: record.canvasId,
        scheduled_at_ms: record.createdAtMs,
        deadline_at_ms: record.submitAtMs,
        grace_period_ms: GENERATION_GRACE_PERIOD_MS,
        remaining_ms: Math.max(0, record.submitAtMs - Date.now()),
        restore_trigger: navigationType === 'reload' ? 'page_refresh' : 'runtime_mount',
        browser_navigation_type: navigationType,
        restored_from_persisted_state: true,
        restore_status: 'waiting',
        backend_request_dispatched: false,
      })
    } else if (record.phase === 'submitting') {
      scheduleSubmissionAckTimeout(key, record)
    }
    notify(key)
  })
}

export function activateGenerationGracePeriod(ownerId: string | null): void {
  if (activeOwnerId === ownerId) return
  timers.forEach((timer) => clearTimeout(timer))
  timers.clear()
  records.clear()
  loadedKeys.clear()
  activeOwnerId = ownerId
  if (ownerId !== null) restoreOwnerRecords(ownerId)
  listeners.forEach((_set, key) => notify(key))
}

export function scheduleGenerationGracePeriod(input: ScheduleGenerationInput): boolean {
  if (!Number.isInteger(input.count) || input.count < 1 || input.count > 6) {
    throw new Error(`Generation count must be an integer from 1 to 6; got ${input.count}`)
  }
  if (activeOwnerId !== null && activeOwnerId !== input.ownerId) {
    throw new Error('Generation owner does not match the active authenticated user')
  }
  const key = generationGraceRequestKey(
    input.ownerId,
    input.projectId,
    input.canvasId,
    input.nodeId,
  )
  const existing = getGenerationGraceRecord(key)
  if (existing?.phase === 'waiting' || existing?.phase === 'submitting') return false
  if (existing?.phase === 'failed') deleteRecord(key)

  const createdAtMs = Date.now()
  const { analytics, ...recordInput } = input
  const record: GenerationGraceRecord = {
    version: 1,
    requestId: crypto.randomUUID(),
    ...recordInput,
    createdAtMs,
    submitAtMs: createdAtMs + GENERATION_GRACE_PERIOD_MS,
    phase: 'waiting',
  }
  if (analytics) submitAnalytics.set(record.requestId, analytics)
  putRecord(key, record)
  scheduleWaitingRecord(key, record)
  log({
    event: 'generation-grace-period:grace-timer-scheduled',
    trace_id: record.requestId,
    node_id: record.nodeId,
    node_type: record.nodeType,
    project_id: record.projectId,
    canvas_id: record.canvasId,
    count: record.count,
    scheduled_at_ms: record.createdAtMs,
    deadline_at_ms: record.submitAtMs,
    grace_period_ms: GENERATION_GRACE_PERIOD_MS,
    backend_request_dispatched: false,
    outcome: 'waiting_for_grace_period',
  })
  return true
}

export function cancelGenerationGracePeriod(key: string): boolean {
  const record = getGenerationGraceRecord(key)
  if (record?.phase !== 'waiting') return false
  deleteRecord(key)
  // Only the node's Stop button reaches here during the grace window.
  trackGenerationCancelled({ projectId: record.projectId, nodeId: record.nodeId, nodeType: record.nodeType, stage: 'grace', requestId: record.requestId })
  log({
    event: 'generation-grace-period:cancelled-before-dispatch',
    trace_id: record.requestId,
    node_id: record.nodeId,
    node_type: record.nodeType,
    project_id: record.projectId,
    canvas_id: record.canvasId,
    elapsed_since_schedule_ms: Date.now() - record.createdAtMs,
    cancel_trigger: 'user_stop_button',
    cancel_phase: 'waiting',
    cancel_skip_reason: 'backend_not_dispatched',
    cancelled_generation_job_count: 0,
    queued_job_ids: [],
    backend_request_dispatched: false,
    outcome: 'cancelled_during_grace_period',
  })
  return true
}

export function acknowledgeGenerationSubmission(key: string): void {
  const record = getGenerationGraceRecord(key)
  if (record?.phase !== 'submitting') return
  deleteRecord(key)
  log({
    event: 'generation-grace-period:acknowledged',
    trace_id: record.requestId,
    node_id: record.nodeId,
    node_type: record.nodeType,
    source: 'backend-node-status',
  })
}

export function clearGenerationGraceFailure(key: string): void {
  const record = getGenerationGraceRecord(key)
  if (record?.phase !== 'failed') return
  deleteRecord(key)
}

export function getGenerationGraceRecord(key: string): GenerationGraceRecord | null {
  const cached = records.get(key)
  if (cached !== undefined) return cached
  if (loadedKeys.has(key)) return null
  loadedKeys.add(key)
  const store = storage()
  const raw = store.getItem(key)
  const parsed = parseRecord(raw)
  if (parsed === null) {
    if (raw !== null) store.removeItem(key)
    return null
  }
  records.set(key, parsed)
  if (parsed.ownerId === activeOwnerId) {
    if (parsed.phase === 'waiting') scheduleWaitingRecord(key, parsed)
    if (parsed.phase === 'submitting') scheduleSubmissionAckTimeout(key, parsed)
  }
  return parsed
}

export function subscribeGenerationGraceRecord(key: string, listener: Listener): () => void {
  const set = listeners.get(key) ?? new Set<Listener>()
  set.add(listener)
  listeners.set(key, set)
  return () => {
    set.delete(listener)
    if (set.size === 0) listeners.delete(key)
  }
}

/** Test-only reset seam. Production callers must never discard durable state. */
export function resetGenerationGracePeriodForTests(options?: { preserveStorage?: boolean }): void {
  timers.forEach((timer) => clearTimeout(timer))
  timers.clear()
  records.clear()
  loadedKeys.clear()
  listeners.clear()
  activeOwnerId = null
  if (options?.preserveStorage) return
  try {
    const store = storage()
    const keys: string[] = []
    for (let index = 0; index < store.length; index += 1) {
      const key = store.key(index)
      if (key?.startsWith(STORAGE_PREFIX)) keys.push(key)
    }
    keys.forEach((key) => store.removeItem(key))
  } catch {
    // Tests that replace sessionStorage with a throwing stub still need reset.
  }
}
